import { Box, TableCell, TableRow } from '@material-ui/core';
import { AgentInLenderContext, formatDateTimeReadable, LenderMap } from '@nemuru-eng/js-api-client';

import { ConditionalWrapper } from '@/components/shared/components/ConditionalWrapper';
import { Font } from '@/components/shared/components/Font';
import { LenderIconAvatar } from '@/components/shared/components/Logo/LenderLogo/LenderIcon';
import { MoreActionsMenu } from '@/components/shared/components/MoreActionsMenu/MoreActionsMenu';
import { MoreActionsMenuWithIcon } from '@/components/shared/components/MoreActionsMenu/MoreActionsMenuWithIcon';
import { Text } from '@/components/shared/components/Text';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { useScreen } from '@/hooks/useScreen';
import { DefaultTableProps } from '@/types/defaultTableProps';

interface AgentInLenderContextRowProps {
  agentInLenderContext: AgentInLenderContext;
  handleEditCredentials: ({ agentCredentials }: { agentCredentials: AgentInLenderContext }) => void;
  handleDeleteCredentials: ({ agentCredentials }: { agentCredentials: AgentInLenderContext }) => void;
  tableProps: Omit<DefaultTableProps, 'isScrollable'>;
  hideColumnsForDeleteMode?: boolean;
  disabled?: boolean;
}

export const AgentInLenderContextRow = ({
  agentInLenderContext,
  handleEditCredentials,
  handleDeleteCredentials,
  tableProps,
  hideColumnsForDeleteMode,
  disabled = false,
}: AgentInLenderContextRowProps) => {
  const { classes } = tableProps;
  const { isMobile } = useScreen();

  const { palette } = useCustomTheme();

  const actionsOptionsArrayByIndex = () => {
    return [
      {
        display: <Text tId={'EDIT_CREDENTIALS'} />,
        iconName: 'edit',
        onClick: () => handleEditCredentials({ agentCredentials: agentInLenderContext }),
      },
      {
        display: <Text tId={'DELETE_CREDENTIALS'} />,
        iconName: 'delete',
        onClick: () => handleDeleteCredentials({ agentCredentials: agentInLenderContext }),
      },
    ];
  };

  const lenderName = LenderMap.fromLenderId(agentInLenderContext.lenderId);

  const desktopItemRow = (
    <TableRow
      key={agentInLenderContext.agentId}
      className={classes.root}
      style={{
        opacity: disabled ? 0.8 : 1,
        maxWidth: '100%',
      }}
    >
      <TableCell align={'center'}>
        <Box display={'flex'} justifyContent={'center'} alignItems={'center'}>
          <Box style={{ filter: disabled ? 'grayscale(100%) opacity(30%)' : 'none' }}>
            {lenderName && <LenderIconAvatar transparent lenderName={lenderName} />}
          </Box>
        </Box>
      </TableCell>
      <TableCell align={'left'}>
        <Box className={'overflow-text-ellipsis'} display={'flex'} alignItems={'center'} style={{ maxWidth: '130px' }}>
          <Font variant={'body2'} style={{ color: disabled ? palette.grey[400] : palette.text.primary }}>
            {agentInLenderContext.merchantIdInLenderContext}
          </Font>
        </Box>
      </TableCell>
      <TableCell align={'left'}>
        <Box className={'overflow-text-ellipsis'} display={'flex'} alignItems={'center'} style={{ maxWidth: '130px' }}>
          <Font variant={'body2'} style={{ color: disabled ? palette.grey[400] : palette.text.primary }}>
            {agentInLenderContext.channelIdInLenderContext || '-'}
          </Font>
        </Box>
      </TableCell>
      <TableCell align={'left'}>
        <div>
          <div className={'overflow-text-ellipsis'} style={{ maxWidth: '130px' }}>
            <Font variant={'body2'} style={{ color: disabled ? palette.grey[400] : palette.text.primary }}>
              {agentInLenderContext.agentIdInLenderContext || '-'}
            </Font>
          </div>
        </div>
      </TableCell>
      <TableCell align={'left'}>
        <Font variant={'body2'} style={{ color: disabled ? palette.grey[400] : palette.text.primary }}>
          <Text tId={agentInLenderContext.product} />
        </Font>
      </TableCell>
      <TableCell align={'left'}>
        <Font variant={'body2'} style={{ color: disabled ? palette.grey[400] : palette.text.primary }}>
          <Text tId={`${agentInLenderContext.program}_SHORT`} />
        </Font>
      </TableCell>
      <TableCell align={'left'}>
        <Font variant={'body2'} style={{ color: disabled ? palette.grey[400] : palette.text.primary }}>
          {agentInLenderContext.articleType ? <Text tId={agentInLenderContext.articleType} /> : '-'}
        </Font>
      </TableCell>
      {!hideColumnsForDeleteMode && (
        <TableCell width={'140px'} align={'left'}>
          <Font variant={'body2'} style={{ color: disabled ? palette.grey[400] : palette.text.primary }}>
            {formatDateTimeReadable(agentInLenderContext.createdAt)}
          </Font>
        </TableCell>
      )}
      {!hideColumnsForDeleteMode && (
        <TableCell align={'right'}>
          <MoreActionsMenuWithIcon actionsOptionsArray={actionsOptionsArrayByIndex()} />
        </TableCell>
      )}
    </TableRow>
  );

  const mobileItemRow = (
    <ConditionalWrapper
      condition={!hideColumnsForDeleteMode}
      wrapper={(children) => (
        <MoreActionsMenu
          actionsOptionsArray={actionsOptionsArrayByIndex()}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          {children}
        </MoreActionsMenu>
      )}
    >
      <TableRow
        key={agentInLenderContext.id}
        className={classes.root}
        style={{
          opacity: disabled ? 0.8 : 1,
        }}
      >
        <TableCell align={'center'}>
          <Box p={1} display={'flex'} justifyContent={'center'} alignItems={'center'}>
            {lenderName && <LenderIconAvatar transparent lenderName={lenderName} />}
          </Box>
        </TableCell>
        <TableCell align="left">
          <Font variant={'body2'} style={{ color: disabled ? palette.grey[400] : palette.text.primary }}>
            {agentInLenderContext.merchantIdInLenderContext}
          </Font>
        </TableCell>
        <TableCell align="left">
          <Font variant={'body2'} style={{ color: disabled ? palette.grey[400] : palette.text.primary }}>
            <Text tId={agentInLenderContext.product} />
          </Font>
        </TableCell>
        <TableCell align="left">
          <Font variant={'body2'} style={{ color: disabled ? palette.grey[400] : palette.text.primary }}>
            <Text tId={agentInLenderContext.program} />
          </Font>
        </TableCell>
        {!hideColumnsForDeleteMode && (
          <TableCell align={'right'}>
            <MoreActionsMenuWithIcon actionsOptionsArray={actionsOptionsArrayByIndex()} />
          </TableCell>
        )}
      </TableRow>
    </ConditionalWrapper>
  );

  return !isMobile ? desktopItemRow : mobileItemRow;
};
